import React from 'react';
import { Copy, Plus, Trash2 } from 'lucide-react';
import { FeatureLimitHint } from '../../../components/FeatureLimitHint';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import { useFeatureLimits } from '../../../hooks/useFeatureLimits';
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { SortablePipelinePhaseRow } from '../../leads/components/SortablePipelinePhaseRow';
import { AppSelect } from '../../../components/AppSelect';
import { leadCategorySwatchClassName } from '../../../utils/leadCategoryColors';
import type { LeadPipeline } from '../../../types';
import type { LeadCategoryRow } from '../types';
import { SettingsCard } from '../components/SettingsCard';
import {
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
  SETTINGS_INPUT_CLASS,
  SETTINGS_FIELD_LABEL,
  SETTINGS_SELECT_TRIGGER_CLASS,
} from '../utils/settingsSectionStyles';

const inputClass = SETTINGS_INPUT_CLASS;
const btnOutline = SETTINGS_BTN_OUTLINE;
const btnPrimary = SETTINGS_BTN_PRIMARY;

type StageDraft = { id?: string; label: string };

function stagesEqual(a: StageDraft[], b: LeadPipeline['stages'] | undefined): boolean {
  const bb = Array.isArray(b) ? b : [];
  if (a.length !== bb.length) return false;
  return a.every((s, i) => s.label === bb[i]?.label && (s.id ?? '') === (bb[i]?.id ?? ''));
}

type LeadPipelineSettingsSectionProps = {
  /** Notify siblings (e.g. categories picker) when the pipeline list changes. */
  onPipelinesChange?: (pipelines: LeadPipeline[]) => void;
  /** Live categories from LeadCategoriesSettingsSection (labels + assignment). */
  categories?: LeadCategoryRow[];
};

export function LeadPipelineSettingsSection({
  onPipelinesChange,
  categories = [],
}: LeadPipelineSettingsSectionProps = {}) {
  const [pipelines, setPipelines] = React.useState<LeadPipeline[]>([]);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [name, setName] = React.useState('');
  const [stages, setStages] = React.useState<StageDraft[]>([]);
  const [phaseDraft, setPhaseDraft] = React.useState('');
  const [loaded, setLoaded] = React.useState(false);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  /** Prevents false dirty while name/stages catch up after pipeline switch. */
  const [draftForId, setDraftForId] = React.useState<string | null>(null);
  const { usage, loaded: limitsReady } = useFeatureLimits();
  const pipelineLimit = usage('pipelines');
  const phaseLimit = usage('pipelinePhases');
  const pipelinesAtLimit = pipelines.length >= pipelineLimit.max;
  const phasesAtLimit = stages.length >= phaseLimit.max;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const selected = pipelines.find((p) => p.id === selectedId) ?? null;
  const defaultPipelineId = pipelines.find((p) => p.is_default)?.id ?? pipelines[0]?.id ?? '';

  const assignedCategories = React.useMemo(() => {
    if (!selected) return [];
    return categories.filter((c) => {
      const pid = c.pipeline_id && String(c.pipeline_id).trim() ? c.pipeline_id : defaultPipelineId;
      return pid === selected.id;
    });
  }, [categories, selected, defaultPipelineId]);

  const isDirty = Boolean(
    selected &&
      draftForId === selected.id &&
      (name.trim() !== selected.name.trim() || !stagesEqual(stages, selected.stages)),
  );

  const load = React.useCallback(async () => {
    setLoaded(false);
    setLoadError(null);
    try {
      const res = await fetch('/api/settings/lead-pipelines', { credentials: 'include' });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error || 'Pipelines konnten nicht geladen werden.');
      }
      const data = (await res.json()) as LeadPipeline[];
      const list = Array.isArray(data) ? data : [];
      setPipelines(list);
      setSelectedId((prev) => {
        if (prev && list.some((p) => p.id === prev)) return prev;
        return list.find((p) => p.is_default)?.id ?? list[0]?.id ?? null;
      });
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Pipelines konnten nicht geladen werden.');
    } finally {
      setLoaded(true);
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  React.useEffect(() => {
    onPipelinesChange?.(pipelines);
  }, [pipelines, onPipelinesChange]);

  React.useLayoutEffect(() => {
    if (!selected) {
      setName('');
      setStages([]);
      setDraftForId(null);
      return;
    }
    setName(selected.name);
    setStages((selected.stages ?? []).map((s) => ({ id: s.id, label: s.label })));
    setPhaseDraft('');
    setError(null);
    setDraftForId(selected.id);
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectPipeline = (nextId: string) => {
    if (nextId === selectedId) return;
    if (isDirty) {
      const ok = window.confirm(
        'Ungespeicherte Änderungen verwerfen und zu einer anderen Pipeline wechseln?',
      );
      if (!ok) return;
    }
    setSelectedId(nextId);
  };

  const handleDragEnd = React.useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = Number.parseInt(String(active.id), 10);
    const newIndex = Number.parseInt(String(over.id), 10);
    if (!Number.isFinite(oldIndex) || !Number.isFinite(newIndex)) return;
    setStages((list) => arrayMove(list, oldIndex, newIndex));
  }, []);

  const save = async () => {
    if (!selected || !isDirty) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/settings/lead-pipelines/${encodeURIComponent(selected.id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name,
          stages: stages.map((s) => ({ id: s.id, label: s.label })),
        }),
      });
      const data = (await res.json().catch(() => ({}))) as LeadPipeline & { error?: string };
      if (!res.ok) {
        setError(data.error || 'Speichern fehlgeschlagen.');
        return;
      }
      setPipelines((prev) => prev.map((p) => (p.id === data.id ? data : p)));
      setName(data.name);
      setStages(data.stages.map((s) => ({ id: s.id, label: s.label })));
    } catch {
      setError('Netzwerkfehler beim Speichern.');
    } finally {
      setSaving(false);
    }
  };

  const createPipeline = async (copyFrom?: string) => {
    if (isDirty) {
      const ok = window.confirm('Ungespeicherte Änderungen verwerfen und neue Pipeline anlegen?');
      if (!ok) return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/settings/lead-pipelines', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: 'Neue Pipeline',
          copy_from_pipeline_id: copyFrom ?? selectedId,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as LeadPipeline & { error?: string };
      if (!res.ok) {
        setError(data.error || 'Anlegen fehlgeschlagen.');
        return;
      }
      setPipelines((prev) => [...prev, data]);
      setSelectedId(data.id);
    } catch {
      setError('Netzwerkfehler beim Anlegen.');
    } finally {
      setSaving(false);
    }
  };

  const removePipeline = async () => {
    if (!selected || selected.is_default) return;
    if (!window.confirm(`Pipeline „${selected.name}“ wirklich löschen?`)) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/settings/lead-pipelines/${encodeURIComponent(selected.id)}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) {
        setError(data.error || 'Löschen fehlgeschlagen.');
        return;
      }
      await load();
    } catch {
      setError('Netzwerkfehler beim Löschen.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsCard
      title="Projekt-Pipelines"
      description="Mehrere Pipelines möglich — Kategorien können dieselbe teilen. „Gewonnen“ und „Verloren“ sind fest."
    >
      {loadError ? (
        <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">{loadError}</div>
      ) : !loaded ? (
        <InlineLoadingState label="Lade…" className="justify-start" />
      ) : (
        <div className="space-y-4">
            {error ? (
              <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">{error}</div>
            ) : null}

            <div className="flex flex-wrap items-center gap-2">
              <AppSelect
                value={selectedId ?? ''}
                onChange={selectPipeline}
                options={pipelines.map((p) => ({
                  value: p.id,
                  label: p.name,
                  badge: p.is_default ? 'Standard' : undefined,
                }))}
                className="w-full sm:w-56"
                triggerClassName={SETTINGS_SELECT_TRIGGER_CLASS}
                aria-label="Pipeline wählen"
              />
              <div className="flex flex-wrap items-center gap-1.5 ml-auto sm:ml-0">
                <button
                  type="button"
                  className={btnOutline}
                  disabled={saving || pipelinesAtLimit}
                  onClick={() => void createPipeline()}
                >
                  <Plus className="w-4 h-4 shrink-0" />
                  Neu
                </button>
                <button
                  type="button"
                  className={btnOutline}
                  disabled={!selected || saving || pipelinesAtLimit}
                  onClick={() => void createPipeline(selectedId ?? undefined)}
                >
                  <Copy className="w-4 h-4 shrink-0" />
                  Duplizieren
                </button>
                <button
                  type="button"
                  className={btnOutline}
                  disabled={!selected || selected.is_default || saving}
                  onClick={() => void removePipeline()}
                  title="Pipeline löschen"
                >
                  <Trash2 className="w-4 h-4 shrink-0" />
                  Löschen
                </button>
                <FeatureLimitHint
                  limitKey="pipelines"
                  used={pipelines.length}
                  max={pipelineLimit.max}
                  ready={limitsReady}
                  className="ml-1 shrink-0"
                />
              </div>
            </div>

            {selected ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2 min-w-0">
                  <label className={`${SETTINGS_FIELD_LABEL} shrink-0`} htmlFor="pipeline-name">
                    Name
                  </label>
                  <input
                    id="pipeline-name"
                    type="text"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                    }}
                    className={`min-w-0 flex-1 max-w-xs ${inputClass}`}
                    maxLength={120}
                  />
                </div>

                {assignedCategories.length > 0 ? (
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-xs text-app-text-subtle shrink-0">Kategorien:</span>
                    {assignedCategories.map((c) => (
                      <span
                        key={c.id}
                        className="inline-flex items-center gap-1.5 max-w-[12rem] rounded-md border border-app-border bg-app-surface px-1.5 py-0.5 text-xs font-medium text-app-text-muted"
                        title={c.label || c.key}
                      >
                        <span
                          className={`h-2 w-2 rounded-full shrink-0 ${leadCategorySwatchClassName(c.color)}`}
                          aria-hidden
                        />
                        <span className="truncate">{c.label.trim() || c.key}</span>
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-app-text-subtle">
                    {selected.is_default
                      ? 'Standard für Kategorien ohne eigene Zuordnung'
                      : 'Keine Kategorie zugeordnet — Zuordnung unter Projekt-Kategorien'}
                  </p>
                )}

                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={stages.map((_, i) => String(i))} strategy={verticalListSortingStrategy}>
                    <ul className="space-y-0">
                      {stages.map((stage, index) => (
                        <SortablePipelinePhaseRow
                          key={stage.id ?? `new-${index}`}
                          id={String(index)}
                          index={index}
                          phase={stage.label}
                          inputClassName={inputClass}
                          canRemove={stages.length > 1}
                          onChange={(i, v) => {
                            setStages((list) => list.map((p, j) => (j === i ? { ...p, label: v } : p)));
                          }}
                          onRemove={(i) => {
                            setStages((list) => {
                              if (list.length <= 1) return list;
                              return list.filter((_, j) => j !== i);
                            });
                          }}
                        />
                      ))}
                    </ul>
                  </SortableContext>
                </DndContext>

                <div className="flex flex-wrap items-center gap-2 pt-2">
                  <div className="flex min-w-[12rem] flex-1 items-center gap-2">
                    <input
                      type="text"
                      value={phaseDraft}
                      onChange={(e) => setPhaseDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter') return;
                        e.preventDefault();
                        const t = phaseDraft.trim();
                        if (!t || stages.length >= phaseLimit.max) return;
                        setStages((list) => [...list, { label: t }]);
                        setPhaseDraft('');
                      }}
                      maxLength={120}
                      placeholder="Neue Phase"
                      className={`min-w-0 flex-1 ${inputClass}`}
                      disabled={phasesAtLimit}
                    />
                    <button
                      type="button"
                      className={`${btnOutline} border-dashed border-pipedrive-blue text-pipedrive-blue hover:bg-blue-50`}
                      disabled={phasesAtLimit || !phaseDraft.trim()}
                      onClick={() => {
                        const t = phaseDraft.trim();
                        if (!t) return;
                        setStages((list) => [...list, { label: t }]);
                        setPhaseDraft('');
                      }}
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Hinzufügen
                    </button>
                    <FeatureLimitHint
                      limitKey="pipelinePhases"
                      used={stages.length}
                      max={phaseLimit.max}
                      ready={limitsReady}
                    />
                  </div>
                  <button
                    type="button"
                    className={
                      isDirty
                        ? btnPrimary
                        : 'inline-flex h-9 items-center px-3.5 text-sm font-medium rounded-md text-app-text-subtle bg-app-surface-muted disabled:opacity-40 shrink-0'
                    }
                    onClick={() => void save()}
                    disabled={saving || !isDirty}
                  >
                    {saving ? 'Speichert…' : 'Speichern'}
                  </button>
                </div>
              </div>
            ) : null}
        </div>
      )}
    </SettingsCard>
  );
}
